import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  Table,
  VisibilityState,
  ColumnFiltersState,
  getFilteredRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import {
  Table as TableComponent,
  TableCell,
  TableContainer,
  TableHeader,
  FilterBar,
  FilterBarRow,
  TableResultCount,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
  Row,
} from '../Table';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from 'design-system/src/components/SaveFiltersButton/SaveFiltersButton';
import { AuthContext } from '../../auth';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { PortalTableFilterOptions } from '../useFetchPortalTableFilters';
import { TableMultiselectFilter } from 'design-system/src/components/TableDropdownFilter/TableDropdownFilter';
import { ProcessingPrescriptionResponseRow } from '../../api';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Pagination } from 'design-system/src/components/Pagination/Pagination';
import { PaginationTableProps } from 'design-system/src/components/Pagination/Pagination';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import { areStringArraysEqual, getDropdownInitialValueFromSearchParams } from '../../utils';
import { reverseNameFormat } from 'shared-utils/utils/formatString';
import { formatStandardDateLocaleString, formatStandardDate } from 'shared-utils/utils/formatDate';
import styled from 'styled-components';
import { TableNullState } from '../TableNullState/TableNullState';
import FormattedCell from 'design-system/src/components/FormattedCell/FormattedCell';
import InfoIcon from 'design-system/src/assets/icons/infoIcon.svg?react';
import { createTrackingProps } from 'shared-utils/analytics';
import { RxFilterValues } from '../../types';
import { getRxTableNullStateType } from './utils';
import { HeaderSortingIndicator } from '../Table';

const MergedRowItem = styled.td`
  text-align: center;
  padding: 2rem;
`;

const CoverageCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const InfoIconContainer = styled(InfoIcon)`
  color: var(--grayscale-600);
  width: 16px;
  height: 16px;
`;

const FixedTable = styled(TableComponent)`
  table-layout: fixed;
`;

const columnHelper = createColumnHelper<ProcessingPrescriptionResponseRow>();

const LoadingComponent = ({ table }: { table: Table<ProcessingPrescriptionResponseRow> }) => {
  return (
    <tr>
      <MergedRowItem colSpan={table.getVisibleLeafColumns().length}>
        <LoadingSpinner height="200px" />
      </MergedRowItem>
    </tr>
  );
};

interface ProcessingRxTableProps {
  data: Array<ProcessingPrescriptionResponseRow>;
  loading: boolean;
  tableProps: PaginationTableProps & {
    // Included to propagate filter values back to RxTablePage
    submitSearchValue: (search: string) => void;
    filters: PortalTableFilterOptions;
    setPrescriberFilter: (prescriberNamesList: string[]) => void;
    setDrugNameFilter: (drugNamesList: string[]) => void;
  };
  initialSearchValue?: string;
  initialFilterValues?: RxFilterValues;
  tabPanelId: string;
  tabId: string;
}

const ProcessingRxTable: React.FC<ProcessingRxTableProps> = ({
  data,
  loading,
  tableProps,
  initialSearchValue,
  initialFilterValues,
  tabPanelId,
  tabId,
}) => {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const drugFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const authContext = React.useContext(AuthContext);

  const initialPrescriberOptions = useMemo(
    () =>
      getDropdownInitialValueFromSearchParams(
        authContext.user?.default_filters,
        initialFilterValues?.prescriber,
        'providers',
        tableProps.filters.prescribers,
      ) as MultiselectAutocompleteOption[],
    [authContext.user?.default_filters, initialFilterValues?.prescriber, tableProps.filters.prescribers],
  );

  const initialDrugOptions = useMemo(
    () =>
      getDropdownInitialValueFromSearchParams(
        authContext.user?.default_filters,
        initialFilterValues?.drug,
        'drugs',
        tableProps.filters.drugs,
      ) as MultiselectAutocompleteOption[],
    [authContext.user?.default_filters, initialFilterValues?.drug, tableProps.filters.drugs],
  );

  const [prescriberSelections, setPrescriberSelections] = useState<string[]>(
    initialPrescriberOptions.map((o) => o.value),
  );
  const [showSaveModal, setShowSaveModal] = useState(false);
  const defaultPrescriberNpis = authContext.user?.default_filters?.providers.map((p) => p.npi) || [];
  const showSaveButton = !areStringArraysEqual(prescriberSelections, defaultPrescriberNpis);

  useEffect(() => {
    setPrescriberSelections(initialPrescriberOptions.map((option) => option.value));
  }, [initialPrescriberOptions]);

  const clearAllFilters = () => {
    // Clear search input
    setSearchValue('');
    tableProps.submitSearchValue('');

    // Clear TableMultiselectFilter components
    prescriberFilterRef.current?.clear();
    drugFilterRef.current?.clear();
    table.resetColumnFilters();
    table.setGlobalFilter('');
  };

  const columns = [
    columnHelper.accessor('created_at', {
      header: 'Date',
      size: DATE_COLUMN_SIZE,
      cell: (info) => formatStandardDateLocaleString(info.getValue()),
      enableSorting: true,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, patient_dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue()?.name ?? ''}
            subtitle={`DOB: ${formatStandardDate(info.getValue()?.patient_dob ?? '')}`}
          />
        ),
        size: DEFAULT_COLUMN_SIZE,
        enableSorting: true,
      },
    ),
    columnHelper.accessor('drug_name', {
      header: 'Drug',
      cell: (info) => {
        const title = info.getValue();
        if (!title) {
          return <FormattedCell id={info.row.original.id} title="-" />;
        }
        return <FormattedCell id={info.row.original.id} title={title} truncateBody />;
      },
      size: DEFAULT_COLUMN_SIZE,
      enableSorting: true,
      filterFn: (row, _columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
        const drugName = (row.original.drug_name || '').toLowerCase();
        return filterValue.some((val: string) => drugName.includes((val || '').toLowerCase()));
      },
    }),
    columnHelper.display({
      id: 'coverage',
      header: 'Coverage',
      cell: () => (
        <CoverageCell>
          Pending
          <Tooltip
            content="This prescription was received as a fax and may take longer to process to ensure accuracy."
            childNotInteractive
            maxWidth="250px"
          >
            <InfoIconContainer title="info" />
          </Tooltip>
        </CoverageCell>
      ),
      size: DEFAULT_COLUMN_SIZE,
    }),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      cell: (info) => {
        const { prescriber_name, provider_npi } = info.row.original;
        if (!prescriber_name && !provider_npi) return emptyTableCellCopy;
        return (
          <FormattedCell
            id={info.row.original.id}
            title={prescriber_name || emptyTableCellCopy}
            subtitle={provider_npi || emptyTableCellCopy}
          />
        );
      },
      size: DEFAULT_COLUMN_SIZE,
      enableSorting: true,
      // Filter by provider NPI
      filterFn: (row, _columnId, filterValue) => {
        if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
        const npi = row.original.provider_npi || '';
        return filterValue.includes(npi);
      },
    }),
  ];

  const [searchValue, setSearchValue] = useState(initialSearchValue || '');

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination: {
        pageIndex: tableProps.pageIndex,
        pageSize: tableProps.perPage,
      },
      columnVisibility,
      columnFilters,
      globalFilter: searchValue,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
    pageCount: Math.ceil(tableProps.total / tableProps.perPage),
    manualPagination: true,
    columnResizeMode: 'onChange',
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
    globalFilterFn: (row, _columnId, filterValue) => {
      // Applies free text search to patient name, drug name, and patient DOB
      if (!filterValue || typeof filterValue !== 'string') return true;
      const term = filterValue.toLowerCase();

      // Convert patient name from "Last, First" to "First Last" format for matching
      const patientNameNormalized = reverseNameFormat(row.original.patient_name || '').toLowerCase();

      return (
        patientNameNormalized.includes(term) ||
        (row.original.drug_name || '').toLowerCase().includes(term) ||
        formatStandardDate(row.original.patient_dob || '')
          .toLowerCase()
          .includes(term)
      );
    },
  });

  // Update client-side filters from table props when switching tabs or when filters are changed
  useEffect(() => {
    // Filter prescriber by NPI, not name
    const prescriberNpis = initialPrescriberOptions.map((option) => option.value);
    const selectedDrugs = initialDrugOptions.map((option) => option.value);

    setColumnFilters([
      { id: 'prescriber_name', value: prescriberNpis },
      { id: 'drug_name', value: selectedDrugs },
    ]);

    table.setGlobalFilter(initialSearchValue || '');
    // table is a stable reference from useReactTable, so we don't need to include it in the deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrescriberOptions, initialDrugOptions, initialSearchValue]);

  const NoResultsComponent = ({
    table,
    initialFilterValues,
  }: {
    table: Table<ProcessingPrescriptionResponseRow>;
    initialFilterValues?: RxFilterValues;
  }) => {
    return (
      <tr>
        <MergedRowItem colSpan={table.getVisibleLeafColumns().length}>
          <TableNullState
            nullStateType={getRxTableNullStateType(initialFilterValues, searchValue)}
            onClearFilters={clearAllFilters}
          />
        </MergedRowItem>
      </tr>
    );
  };

  return (
    <TableContainer>
      <FilterBar>
        <FilterBarRow>
          <TableMultiselectFilter
            ref={prescriberFilterRef}
            options={tableProps.filters.prescribers}
            label={'Prescriber'}
            initialValue={initialPrescriberOptions}
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              // Filter by NPI and sync with table
              const selectedNpisList = value.map((valueItem) => valueItem.value);
              setPrescriberSelections(selectedNpisList);
              table.getColumn('prescriber_name')?.setFilterValue(selectedNpisList);
              tableProps.setPrescriberFilter(selectedNpisList);
            }}
            {...createTrackingProps({
              elementName: 'Processing Prescriptions Table: Prescriber Filter',
              unmask: true,
            })}
          />
          <TableMultiselectFilter
            ref={drugFilterRef}
            options={tableProps.filters.drugs}
            label={'Drug'}
            initialValue={initialDrugOptions}
            onChange={(_, value: MultiselectAutocompleteOption[]) => {
              const selectedValuesList = value.map((valueItem) => valueItem.value);
              table.getColumn('drug_name')?.setFilterValue(selectedValuesList);
              tableProps.setDrugNameFilter(selectedValuesList);
            }}
            {...createTrackingProps({ elementName: 'Processing Prescriptions Table: Drug Filter', unmask: true })}
          />
          <SaveFiltersButton
            $visible={showSaveButton}
            onClick={() => setShowSaveModal(true)}
            {...createTrackingProps({ elementName: 'Processing Prescriptions Table: Save Filters Button' })}
          />

          <TableResultCount count={tableProps.total} />
        </FilterBarRow>
      </FilterBar>

      <FixedTable role="tabpanel" id={tabPanelId} aria-labelledby={tabId}>
        <thead {...createTrackingProps({ unmask: true })}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHeader
                  key={header.id}
                  onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                  $width={`${header.getSize()}px`}
                  $sortable={header.column.getCanSort()}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <HeaderSortingIndicator
                      isSorted={header.column.getIsSorted()}
                      sortDirection={header.column.getIsSorted()}
                    />
                  )}
                </TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {loading ? (
            <LoadingComponent table={table} />
          ) : table.getRowModel().rows.length === 0 ? (
            <NoResultsComponent table={table} initialFilterValues={initialFilterValues} />
          ) : (
            table.getRowModel().rows.map((row) => (
              <Row key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} $width={`${cell.column.getSize()}px`}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </Row>
            ))
          )}
        </tbody>
      </FixedTable>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            {
              label: 'Prescriber',
              displayValues: prescriberSelections.map(
                (npi) => tableProps.filters.prescribers.find((p) => p.value === npi)?.description || npi,
              ),
              saveValues: prescriberSelections,
            },
          ]}
        />
      )}
      {tableProps.total > tableProps.perPage && (
        <Pagination table={table} goBack={tableProps.goBack} goForward={tableProps.goForward} />
      )}
    </TableContainer>
  );
};

export default ProcessingRxTable;
